import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  PassDetails,
  PassInformationDisplayString,
  PassInformationPageResultEnum,
  isCashPass,
  isFlexPass,
} from '../../types/pass.type';

import { AlertService } from '../../services/alert.service';
import { INVALID_DATE, toDateTime } from '../../helpers/date-helpers';
import { DecimalPipe } from '@angular/common';
import { BaseJob } from '../../types/job.type';
import { ModalController, IonicModule } from '@ionic/angular';
import { TranslateService } from '../../services/translate.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-pass-information',
  templateUrl: './pass-information.page.html',
  styleUrls: ['./pass-information.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonicModule, TranslatePipe],
})
export class PassInformationPage implements OnInit {
  private modalCtrl = inject(ModalController);
  private alertService = inject(AlertService);
  private numberPipe = inject(DecimalPipe);
  private translateService = inject(TranslateService);

  @Input()
  passInformation: PassDetails;

  @Input()
  job: BaseJob;

  insufficientErrorMessage = '';
  displayStrings: PassInformationDisplayString[] = [];

  // Status balance expiration

  ngOnInit() {
    const passInformation = this.passInformation;
    if (!passInformation) {
      this.insufficientErrorMessage =
        this.translateService.translate('LABEL.cannotVerifyBalance') + '.';
      this.displayStrings.push({
        label: this.translateService.translate('LABEL.rideCost'),
        value: `$${this.numberPipe.transform(this.job.rideFare, '.2-2')}`,
      });
    } else {
      if (!passInformation.passIdIsValid) {
        this.insufficientErrorMessage = this.translateService
          .translate('LABEL.insufficientErrorMessage')
          .toUpperCase();
      } else if (!passInformation.isActive) {
        this.insufficientErrorMessage = this.translateService
          .translate('LABEL.inactivePass')
          .toUpperCase();
      } else if (passInformation.isExpired) {
        this.insufficientErrorMessage = this.translateService
          .translate('LABEL.passExpired')
          .toUpperCase();
        this.displayStrings.push({
          label: this.translateService.translate('LABEL.passEffective'),
          value: `${this.formatDateString(passInformation.effectiveDate)}`,
        });
        this.displayStrings.push({
          label: this.translateService.translate('LABEL.passExpiration'),
          value: `${this.formatDateString(passInformation.expirationDate)}`,
          isError: true,
        });
      } else if (passInformation.isNotYetEffective) {
        this.insufficientErrorMessage = this.translateService
          .translate('LABEL.passNotEffective')
          .toUpperCase();
        this.displayStrings.push({
          label: this.translateService.translate('LABEL.effectiveDate'),
          value: `${this.formatDateString(passInformation.effectiveDate)}`,
          isError: true,
        });
        this.displayStrings.push({
          label: this.translateService.translate('LABEL.passExpiration'),
          value: `${this.formatDateString(passInformation.expirationDate)}`,
        });
      } else if (isCashPass(passInformation.passType)) {
        // dollars insufficient
        this.insufficientErrorMessage = this.translateService
          .translate('LABEL.insufficientFundsPass')
          .toUpperCase();
        this.displayStrings.push({
          label: this.translateService.translate('LABEL.rideCost'),
          value: `$${this.formatCurrency(passInformation.debitAmoutForRide)}`,
        });
        this.displayStrings.push({
          label: this.translateService.translate('LABEL.passAmount'),
          value: `$${this.formatCurrency(passInformation.remainingBalance)}`,
          isError: true,
        });
      } else if (isFlexPass(passInformation.passType)) {
        // expiration dates OR num rides remaining is insufficient
        this.insufficientErrorMessage = this.translateService
          .translate('LABEL.insufficientRidesPass')
          .toUpperCase();
        this.displayStrings.push({
          label: this.translateService.translate('LABEL.ridesRemain'),
          value: `${passInformation.remainingRides}`,
          isError: true,
        });
        this.displayStrings.push({
          label: this.translateService.translate('LABEL.passExpiration'),
          value: `${this.formatDateString(passInformation.expirationDate)}`,
        });
      }
    }
  }

  formatDateString(date: Date) {
    if (!date) {
      return '';
    }
    const dateTime = toDateTime(date);
    return dateTime.isValid ? dateTime.toFormat('MM/dd/yyyy') : INVALID_DATE;
  }

  formatCurrency(val: number) {
    return this.numberPipe.transform(val, '.2-2');
  }

  async statusOkClicked() {
    const data = {
      status: PassInformationPageResultEnum.Sufficient,
    };
    await this.modalCtrl.dismiss(data);
  }

  async provideRideClicked() {
    let data = { status: PassInformationPageResultEnum.InsufficientProvide };
    if (!this.passInformation) {
      data = { status: PassInformationPageResultEnum.ServerOfflineProvide };
    } else if (!this.passInformation.passIdIsValid) {
      data = { status: PassInformationPageResultEnum.Invalid };
    }
    await this.modalCtrl.dismiss(data);
  }

  denyRideClicked() {
    this.alertService.presentAlert({
      header: this.translateService.translate('ACTION.confirm'),
      message: `${this.translateService.translate('ACTION.confirmDenyRide')}`,
      buttons: [
        {
          text: 'No',
          handler: () => {
            /* Do nothing */
          },
          cssClass: 'primary ',
        },
        {
          text: 'Yes',
          handler: async () => {
            const data = { status: PassInformationPageResultEnum.Deny };
            await this.modalCtrl.dismiss(data);
          },
          cssClass: 'danger ',
        },
      ],
      backdropDismiss: false,
    });
  }
}
